import { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

// Module-level variable to persist scroll position across component remounts
// This is necessary because the parent ChatRuntimeWrapper uses key={runtimeKey}
// which causes this component to be completely remounted when switching threads
let savedScrollPosition = null;

// Module-level variable to persist scroll position during delete operations
// (delete doesn't cause remount, but we need to restore after React re-renders)
let deleteScrollPosition = null;

// Format date helper
const formatDate = (timestamp) => {
  if (!timestamp) return '';

  const now = Math.floor(Date.now() / 1000);
  const diffSecs = now - timestamp;
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffSecs / 3600);
  const diffDays = Math.floor(diffSecs / 86400);

  if (diffSecs < 0 || diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return new Date(timestamp * 1000).toLocaleDateString();
};

// Get thread preview text
const getThreadPreview = (thread) => {
  if (thread.title) {
    return thread.title.length > 50 ? thread.title.slice(0, 50) + '...' : thread.title;
  }
  return 'New conversation';
};

export const ChatHistory = ({
  threads = [],
  currentThreadId,
  onSelectThread,
  onCloseMobile,
  onRenameThread,
  onDeleteThread
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuThread, setMenuThread] = useState(null);
  const scrollContainerRef = useRef(null);

  // Restore scroll position on mount (after component remounts due to runtimeKey change)
  useEffect(() => {
    if (savedScrollPosition !== null && scrollContainerRef.current) {
      const targetScroll = savedScrollPosition;
      // Use multiple attempts to handle async rendering
      const restoreScroll = () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = targetScroll;
        }
      };
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
      setTimeout(() => {
        restoreScroll();
        savedScrollPosition = null; // Clear after restoration
      }, 200);
    }
  }, []); // Run once on mount

  // Restore scroll position after delete (threads change but no remount)
  useEffect(() => {
    if (deleteScrollPosition !== null && scrollContainerRef.current) {
      const targetScroll = deleteScrollPosition;
      const restoreScroll = () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = targetScroll;
        }
      };
      // Restore immediately and with multiple attempts
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 0);
      setTimeout(() => {
        restoreScroll();
        deleteScrollPosition = null; // Clear after restoration
      }, 50);
    }
  }, [threads.length]); // Run when threads count changes (after delete)

  const handleSelectThread = async (threadId) => {
    // Don't do anything if clicking the same thread
    if (threadId === currentThreadId) return;

    // Save scroll position to module-level variable (survives remount)
    savedScrollPosition = scrollContainerRef.current?.scrollTop || 0;

    await onSelectThread(threadId);

    // Close mobile sidebar after selection
    if (window.innerWidth <= 768 && onCloseMobile) {
      onCloseMobile();
    }
  };

  const handleMenuOpen = (e, thread) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuThread(thread);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuThread(null);
  };

  const handleRename = async () => {
    if (!menuThread) return;
    const newTitle = prompt('Enter new name:', getThreadPreview(menuThread));
    if (newTitle && newTitle.trim()) {
      await onRenameThread(menuThread.id, newTitle.trim());
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!menuThread) return;
    // Save scroll position to module-level variable before delete
    // This will be restored by the useEffect when threads.length changes
    deleteScrollPosition = scrollContainerRef.current?.scrollTop || 0;
    await onDeleteThread(menuThread.id);
    handleMenuClose();
  };

  return (
    <Box
      ref={scrollContainerRef}
      id="historyScroll"
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 1.5,
        py: 1.5,
        minHeight: 0,
        overflowAnchor: 'none', // Prevent browser's automatic scroll anchoring
      }}
    >
      {/* Header */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          px: 0.5,
          pb: 1.5,
          color: 'text.secondary',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        History
      </Typography>

      {/* Thread List */}
      <List disablePadding id="historyList">
        {threads.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: 'text.disabled', py: 4 }}
          >
            No conversations yet
          </Typography>
        ) : (
          threads.map((thread) => (
            <ListItemButton
              key={thread.id}
              selected={currentThreadId === thread.id}
              onClick={() => handleSelectThread(thread.id)}
              dense
              sx={{
                borderRadius: 1,
                mb: 0.25,
                py: 0.25,
                px: 1,
                minHeight: 0,
                position: 'relative',
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                },
              }}
            >
              <ListItemText
                primary={getThreadPreview(thread)}
                secondary={formatDate(thread.created_timestamp)}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: 500,
                  noWrap: true,
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                }}
                sx={{ mr: 1 }}
              />
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, thread)}
                sx={{
                  p: 0.5,
                  color: 'text.secondary',
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <MoreHorizIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </ListItemButton>
          ))
        )}
      </List>

      {/* Context Menu for Edit/Delete */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleRename}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Rename
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};
